/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

// Medaica Report patient-view result cards at visual fidelity with the
// prototype (renderCardView patient branch, cardiac_report_v0_5_6_dev_2.html
// lines 5958-6205): .biomarker-card grid, traffic-light scale with per-feature
// gradient + zone labels, reference range, "What this means" note. The card
// header carries the title, the patient result wording and the ⓘ tooltip
// only — the prototype renders no status chip in patient report mode (the
// derived status is exposed as data-status for tests and styling hooks).
// Styling lives in heart-report.css, scoped under .pcg-heart-report.
// Patient surface only (the handoff's third-party evidence spec (`pcg_exam_viewer_third_party_evidence_spec.md`) §16):
// no confidence, provenance, or workflow traces.

import React from "react"
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore vendored plain-JS core with hand-written surface types
import {
  reportFeatures,
  currentHeartRateBpm,
  effectiveFeatureColorClass,
  reportCardTitle,
  reportResultText,
  displayReferenceRange,
  patientNoteDisplay,
  generateInterpretation,
  getFeatureDescription,
} from "./core"
// The scale (track variant, zone labels, marker position) is computed by the
// shared scale module so the patient report document carries exactly what
// this card renders. The gradients themselves live in heart-report.css
// (.scale-track--uni / .scale-track--bi).
import { ScaleRange, scaleLabels, scaleMarkerPct, scaleRangePct, scaleTrackVariant } from "./scale"

/**
 * A timing card's range on the track (Heart Rate, SYS:DIA Ratio or SYS:DIA
 * Duration, Heart Report v0.5.8): a marker at each end joined by a band. The
 * graphic is decorative — the range itself is read from the card's observed
 * value — so the three pieces are hidden from assistive technology and carry
 * their positions as data attributes; each end's title names its value.
 */
export const ScaleRangeMarkers: React.FC<{ range: ScaleRange; name: string }> = ({ range, name }) => (
  <>
    <div
      className="phr-scale-range"
      style={{ left: `${range.minPct}%`, width: `${Math.max(2, range.maxPct - range.minPct)}%` }}
      aria-hidden="true"
      data-testid={`range-band-${name}`}
    />
    <div
      className="scale-marker"
      style={{ left: `${range.minPct}%` }}
      aria-hidden="true"
      title={range.minLabel}
      data-marker-min={range.minPct.toFixed(2)}
    />
    <div
      className="scale-marker"
      style={{ left: `${range.maxPct}%` }}
      aria-hidden="true"
      title={range.maxLabel}
      data-marker-max={range.maxPct.toFixed(2)}
    />
  </>
)

interface FeatureLike {
  name: string
  markerPosition?: number
  type?: string
  scaleType?: string
  showNumericInPatient?: boolean
  [key: string]: unknown
}

export const HeartReportCards: React.FC<{ features: FeatureLike[] }> = ({ features }) => {
  const hr = currentHeartRateBpm(features as never)
  const visible = reportFeatures(features as never, { viewMode: "patient" }) as FeatureLike[]

  if (visible.length === 0) {
    return (
      <div className="category-grid" data-testid="heart-report-cards">
        <div className="empty-state">No visible panel components</div>
      </div>
    )
  }

  return (
    <div className="category-grid" data-testid="heart-report-cards">
      {visible.map((feature) => {
        const colorClass = effectiveFeatureColorClass(feature as never, hr)
        // Patient view simplifies numerics: only bpm-type cards (or cards
        // explicitly opted in) show observed values + reference ranges.
        const showNumeric = feature.type === "bpm" || feature.showNumericInPatient === true
        const markerPos = scaleMarkerPct(feature as never, hr)
        const range = scaleRangePct(feature as never, hr)
        const note = patientNoteDisplay(feature as never, hr)
        const interpretation = note ? "" : generateInterpretation(feature as never, colorClass)
        const referenceRange = showNumeric ? displayReferenceRange(feature as never, hr) : ""
        const resultText = reportResultText(feature as never, colorClass, { patientFacing: true })
        const description = getFeatureDescription(feature.name)
        return (
          <article
            key={feature.name}
            className={`biomarker-card${showNumeric ? "" : " traffic-light-card"}`}
            data-testid={`card-${feature.name}`}
            data-status={colorClass}
          >
            <div className="card-header">
              <div className="card-title">
                {reportCardTitle(feature as never)}
                {resultText ? (
                  <span className="title-result" data-testid={`result-${feature.name}`}>
                    {resultText}
                  </span>
                ) : null}
              </div>
              <div className="card-header-meta">
                {description ? (
                  <span className="title-help" data-help={description} aria-label={description} tabIndex={0}>
                    i
                  </span>
                ) : null}
              </div>
            </div>
            <div className="scale-container">
              <div className={`scale-track ${scaleTrackVariant(feature as never)}`}>
                {range ? (
                  <ScaleRangeMarkers range={range} name={feature.name} />
                ) : (
                  <div
                    className="scale-marker"
                    style={{ left: `${markerPos}%` }}
                    role="img"
                    aria-label={`Scale marker at ${markerPos.toFixed(0)} percent`}
                    data-marker={markerPos.toFixed(2)}
                  />
                )}
              </div>
              <div className="scale-labels">
                {scaleLabels(feature as never).map((label, i) => (
                  <div key={`${label.text}-${i}`} className="scale-label" style={{ flex: `0 0 ${label.basisPct}%` }}>
                    {label.text}
                  </div>
                ))}
              </div>
            </div>
            {referenceRange ? (
              <div className="card-range" data-testid={`range-${feature.name}`}>
                {referenceRange}
              </div>
            ) : null}
            {note ? (
              <div className="note-patient-display" data-testid={`note-${feature.name}`}>
                <span className="note-label">What this means</span>
                {note}
              </div>
            ) : (
              <div className="card-interpretation">{interpretation}</div>
            )}
          </article>
        )
      })}
    </div>
  )
}

export default HeartReportCards
